import { useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useFormAPI, useFormStore } from "@/widgets/classic-form/form";
import type { MatchMode } from "@/widgets/classic-form/form/types";
import { ExactnessCheckbox } from "@/features/form-fields/field-exactness";
import { useMatchAllTermsContext } from "@/features/match-all-terms";
import type { TimespanAttributes } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import { Show } from "@/shared/components/Show";
import { AddTimespanPanel } from "./Panel/AddTimespanPanel";
import { TimespanCard } from "./TimespanCard";
import { YearInputs } from "./YearInputs";
import styles from "./Timespan.module.scss";

export function Timespan({ label, sliderRange, usageHelpText, historicalPeriods }: TimespanAttributes) {
    const { t } = useTranslation();
    const { setFieldValue } = useFormAPI();
    const { matchAllTermsExactly, matchAllTermsSupported } = useMatchAllTermsContext();
    const isTimespanEnabled = useFormStore(({ values }) => {
        return values.timespan.isVisible;
    });
    const hasBothYears = useFormStore(({ values }) => {
        const timespan = values.timespan;
        return timespan.yearStart != null && timespan.yearEnd != null;
    });

    const handleAddTimespan = useCallback(() => {
        const matchMode = matchAllTermsSupported && matchAllTermsExactly ? "mustMatch" : "rankOnly";
        setFieldValue("timespan", {
            yearStart: sliderRange.min,
            yearEnd: sliderRange.max,
            matchMode,
            isVisible: true,
        });
    }, [setFieldValue, sliderRange.min, sliderRange.max, matchAllTermsSupported, matchAllTermsExactly]);

    const handleRemoveTimespan = useCallback(() => {
        setFieldValue("timespan", {
            yearStart: null,
            yearEnd: null,
            matchMode: "rankOnly",
            isVisible: false,
        });
    }, [setFieldValue]);

    function isTimespanExact(matchMode: MatchMode) {
        return matchMode === "mustMatch";
    }

    function toggleTimespanMatchMode(matchMode: MatchMode) {
        setFieldValue("timespan.matchMode", matchMode === "rankOnly" ? "mustMatch" : "rankOnly");
    }

    const RemoveButton = (
        <button
            type="button"
            className={classNames("ancBtn sml silver noBorder", styles.removeBtn)}
            onClick={handleRemoveTimespan}
            aria-label={t("timespan.removeEstimatedTimespan")}
        >
            <span aria-hidden="true" className={classNames("icon iconRemove", styles.removeIcon)} />
        </button>
    );

    return (
        <div className={styles.root}>
            <AddTimespanPanel
                onItemAdded={handleAddTimespan}
                disabled={isTimespanEnabled}
                label={label}
                usageHelpText={usageHelpText}
            />

            {isTimespanEnabled && (
                <Grid.Container className={styles.timespanRow}>
                    <Grid.Field spanDesktop={2} spanTablet={12} spanMobile={12}>
                        <Show.Above breakpoint={700}>
                            <span className={styles.timespanLabel}>{label}</span>
                        </Show.Above>

                        <Show.Below breakpoint={700}>
                            <Grid.Container className={styles.mobileLabelRow}>
                                <Grid.Field spanTablet={11} spanMobile={11}>
                                    <span className={styles.timespanLabel}>{label}</span>
                                </Grid.Field>
                                <Grid.Field spanTablet={1} spanMobile={1}>
                                    <div className={styles.removeBtnContainer}>{RemoveButton}</div>
                                </Grid.Field>
                            </Grid.Container>
                        </Show.Below>
                    </Grid.Field>

                    <Grid.Field spanDesktop={9} spanTablet={12} spanMobile={12}>
                        <TimespanCard
                            sliderRange={sliderRange}
                            startFieldPath="timespan.yearStart"
                            endFieldPath="timespan.yearEnd"
                            historicalPeriods={historicalPeriods}
                        />

                        <YearInputs
                            min={sliderRange.min}
                            max={sliderRange.max}
                            startFieldPath="timespan.yearStart"
                            endFieldPath="timespan.yearEnd"
                        />

                        <ExactnessCheckbox<MatchMode>
                            show={hasBothYears}
                            name="timespan.matchMode"
                            inputValue={String(sliderRange.min)}
                            isExactFn={isTimespanExact}
                            onToggleExact={toggleTimespanMatchMode}
                        />
                    </Grid.Field>

                    <Show.Above breakpoint={700}>
                        <Grid.Field spanDesktop={1} spanTablet={1} spanMobile={1}>
                            <div className={styles.removeBtnContainer}>{RemoveButton}</div>
                        </Grid.Field>
                    </Show.Above>
                </Grid.Container>
            )}
        </div>
    );
}
